// Sprint 1 — Onboarding post-registro
import { useState } from "react";
import { css, cx } from "styled-system/css";
import { cf } from "@/components/brand/sfturnos-styles";
import { useOnboardingWizard } from "@/context/onboarding-wizard-context";
import { rubroSubOptionLabel } from "@/lib/rubro-catalog";
import { fetchWithSessionSync } from "@/lib/fetch-with-session";
import { useAuth } from "@/providers/AuthProvider";

type Props = {
  onDone: () => void;
};

type DraftRow = {
  key: string;
  name: string;
  duration: string;
  price: string;
  sport: string | null;
};

let rowSeq = 0;
function newRow(name: string, duration: string, sport: string | null = null): DraftRow {
  rowSeq += 1;
  return { key: `row-${rowSeq}`, name, duration, price: "", sport };
}

export function OnboardingStepFirstOffering({ onDone }: Props) {
  const { state } = useOnboardingWizard();
  const { refresh } = useAuth();
  const isFacility = state.accountType === "FACILITY";

  const [rows, setRows] = useState<DraftRow[]>(() => {
    if (state.subRubro.length > 0) {
      return state.subRubro.map((id) => {
        const label = rubroSubOptionLabel(state.rubro, id);
        return newRow(isFacility ? `Cancha de ${label}` : `Clases de ${label}`, isFacility ? "60" : "30", id);
      });
    }
    return [newRow("", isFacility ? "60" : "30")];
  });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  function patchRow(key: string, patch: Partial<DraftRow>) {
    setRows((prev) => prev.map((r) => (r.key === key ? { ...r, ...patch } : r)));
  }

  function addRow() {
    setRows((prev) => [...prev, newRow("", isFacility ? "60" : "30")]);
  }

  function removeRow(key: string) {
    setRows((prev) => (prev.length <= 1 ? prev : prev.filter((r) => r.key !== key)));
  }

  async function handleSave() {
    setErr(null);
    const valid = rows.filter((r) => r.name.trim().length > 0);
    if (valid.length === 0) {
      setErr("Cargá al menos un nombre para continuar, o elegí omitir este paso.");
      return;
    }
    setBusy(true);
    try {
      for (const row of valid) {
        const duration = Number.parseInt(row.duration, 10) || (isFacility ? 60 : 30);
        const priceRaw = row.price.trim();
        const price = priceRaw === "" ? null : Number(priceRaw);
        const url = isFacility ? "/api/dashboard/courts" : "/api/dashboard/services";
        const body = isFacility
          ? { name: row.name.trim(), sport: row.sport, durationMinutes: duration, pricePerSlot: price }
          : { name: row.name.trim(), duration, price };
        const r = await fetchWithSessionSync(
          url,
          { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify(body) },
          refresh,
        );
        if (!r.ok) {
          const j = (await r.json().catch(() => ({}))) as { error?: string };
          throw new Error(j.error ?? "No se pudo guardar");
        }
      }
      onDone();
    } catch (e) {
      setErr(e instanceof Error ? e.message : "No se pudo guardar");
    } finally {
      setBusy(false);
    }
  }

  return (
    <div>
      <h1 className={css({ fontSize: "2xl", fontWeight: "bold", color: "#0f172a", mb: "2" })}>
        {isFacility ? "Cargá tus canchas" : "Cargá tu primer servicio"}
      </h1>
      <p className={css({ fontSize: "sm", color: "#64748b", mb: "8", maxW: "42rem", lineHeight: 1.6 })}>
        {isFacility
          ? "Sin al menos una cancha cargada todavía no vas a poder recibir reservas. Después vas a poder editar precio, superficie y capacidad en Canchas."
          : "Sin al menos un servicio cargado todavía no vas a poder recibir turnos. Después vas a poder sumar más u editar precio y duración en Servicios."}
      </p>

      <div className={css({ display: "flex", flexDir: "column", gap: "4", mb: "6" })}>
        {rows.map((row) => (
          <div
            key={row.key}
            className={cx(
              cf.dashSurface,
              css({
                p: "4",
                display: "grid",
                gap: "3",
                gridTemplateColumns: { base: "1fr", sm: "2fr 1fr 1fr auto" },
                alignItems: "end",
              }),
            )}
          >
            <label className={css({ display: "flex", flexDir: "column", gap: "1" })}>
              <span className={css({ fontSize: "xs", color: "#64748b" })}>
                {isFacility ? "Nombre de la cancha" : "Nombre del servicio"}
              </span>
              <input
                value={row.name}
                onChange={(e) => patchRow(row.key, { name: e.target.value })}
                className={cf.input}
                placeholder={isFacility ? "Ej. Cancha 1" : "Ej. Consulta médica"}
              />
            </label>
            <label className={css({ display: "flex", flexDir: "column", gap: "1" })}>
              <span className={css({ fontSize: "xs", color: "#64748b" })}>Duración (min)</span>
              <input
                type="number"
                min={5}
                step={5}
                value={row.duration}
                onChange={(e) => patchRow(row.key, { duration: e.target.value })}
                className={cf.input}
              />
            </label>
            <label className={css({ display: "flex", flexDir: "column", gap: "1" })}>
              <span className={css({ fontSize: "xs", color: "#64748b" })}>Precio (opcional)</span>
              <input
                type="number"
                min={0}
                step={0.01}
                value={row.price}
                onChange={(e) => patchRow(row.key, { price: e.target.value })}
                className={cf.input}
              />
            </label>
            <button
              type="button"
              onClick={() => removeRow(row.key)}
              disabled={rows.length <= 1}
              aria-label="Quitar"
              className={css({
                h: "10",
                px: "3",
                rounded: "md",
                border: "none",
                bg: "transparent",
                color: rows.length <= 1 ? "#cbd5e1" : "#94a3b8",
                cursor: rows.length <= 1 ? "not-allowed" : "pointer",
                fontSize: "lg",
              })}
            >
              ×
            </button>
          </div>
        ))}
      </div>

      <button
        type="button"
        onClick={addRow}
        className={css({
          fontSize: "sm",
          color: "token(colors.brand.600)",
          bg: "transparent",
          border: "none",
          cursor: "pointer",
          fontWeight: "medium",
          mb: "8",
        })}
      >
        + {isFacility ? "Agregar otra cancha" : "Agregar otro servicio"}
      </button>

      {err ? <p className={css({ fontSize: "sm", color: "#b91c1c", mb: "4" })}>{err}</p> : null}

      <div className={css({ display: "flex", alignItems: "center", gap: "5", flexWrap: "wrap" })}>
        <button
          type="button"
          disabled={busy}
          onClick={() => void handleSave()}
          className={cx(
            cf.linkPrimaryOnDark,
            css({
              px: "6",
              py: "3",
              border: "none",
              fontSize: "sm",
              cursor: busy ? "wait" : "pointer",
              opacity: busy ? 0.75 : 1,
              bg: "linear-gradient(90deg, token(colors.brand.600), token(colors.brand.500))",
            }),
          )}
        >
          {busy ? "Guardando…" : "Guardar y continuar"}
        </button>
        <button
          type="button"
          onClick={onDone}
          className={css({
            fontSize: "sm",
            color: "#64748b",
            bg: "transparent",
            border: "none",
            cursor: "pointer",
            textDecoration: "underline",
          })}
        >
          Omitir por ahora
        </button>
      </div>
      <p className={css({ fontSize: "xs", color: "#94a3b8", mt: "3" })}>
        Si omitís este paso, te lo vamos a volver a recordar en tu Panel.
      </p>
    </div>
  );
}
